import { useQuery } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import Spinner from "../../components/Spinner";
import { useToast } from "../../components/Toast/toastHook";
import FavoriteButton from "../../components/FavoriteButton";
import { GET_AGENT_LISTING_BY_ID } from "../../graphql/agentQueries";
import priceFormat from "../../utils/priceFormat";
import fullDate from "../../utils/fullDate";
import { Tag } from "antd";
import { MailOutlined, PhoneOutlined } from "@ant-design/icons";

const AgentListingView = () => {
  const { companyId, listingId } = useParams();
  const navigate = useNavigate();
  const toast = useToast(4000);
  const currentUser = JSON.parse(localStorage.getItem("userInfo"));

  const { loading, error, data } = useQuery(GET_AGENT_LISTING_BY_ID, {
    variables: { listingId },
    fetchPolicy: "network-only",
    skip: !listingId,
  });

  const listing = data?.getListingById;

  // Format property category for display
  const formatPropertyCategory = (category) => {
    if (!category) return null;
    const categoryMap = {
      under_construction: "New/Under Construction",
      land: "Land/Lot",
      sale: "For Sale",
      rent: "For Rent",
    };
    return categoryMap[category] || category;
  };

  // Format property type for display
  const formatPropertyType = (type) => {
    if (!type) return null;
    const typeMap = {
      sale: "For Sale",
      rent: "For Rent",
    };
    return typeMap[type] || type;
  };

  // Check if listing is live (expiration >= today)
  const isLive = () => {
    if (!listing?.expiration) return false;
    const expirationDate = new Date(parseInt(listing.expiration, 10));
    expirationDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expirationDate >= today;
  };

  // Calculate days on market
  const getDaysOnMarket = () => {
    if (!listing?.expiration) return 0;
    const expirationDate = new Date(parseInt(listing.expiration, 10));
    const today = new Date();
    const diffTime = today - expirationDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 ? diffDays : 0;
  };

  if (loading) return <Spinner />;

  if (error) {
    toast("error", error.message);
    return (
      <div className="p-6">
        <p className="text-red-500">Error loading listing: {error.message}</p>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="p-6">
        <p>Listing not found</p>
      </div>
    );
  }

  return (
    <div id="agent-listing-view">
      <Breadcrumb pageName="Listing Profile" />
      <div className="pb-6">
        <Card>
          <div className="p-6">
            {/* Header with Favorite button */}
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-semibold text-secondary">
                Listing Details
              </h1>
              <div className="flex items-center gap-3">
                <FavoriteButton
                  listingId={listingId}
                  agentId={currentUser?.id}
                  companyId={companyId}
                />
              </div>
            </div>

            {/* Photos Section */}
            <div className="mb-6">
              {listing.imageURLs && listing.imageURLs.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {listing.imageURLs.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`Listing ${index + 1}`}
                      className="w-full h-40 object-cover rounded-lg"
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-gray-100 h-48 flex items-center justify-center rounded-lg">
                  <p className="text-gray-500">No photos</p>
                </div>
              )}
            </div>

            {/* Map Section */}
            {listing.coordinates && listing.coordinates.length === 2 && (
              <div className="mb-6">
                <iframe
                  title="Listing Location"
                  width="100%"
                  height="200"
                  style={{ border: 0, borderRadius: "8px" }}
                  loading="lazy"
                  src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${listing.coordinates[1]},${listing.coordinates[0]}`}
                />
              </div>
            )}

            {/* Price and Status */}
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="flex gap-2 mb-2">
                  {listing.propertyType && (
                    <span className="inline-block px-3 py-1 bg-primary text-white rounded-full text-sm">
                      {formatPropertyType(listing.propertyType)}
                    </span>
                  )}
                  {listing.propertyCategory && (
                    <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                      {formatPropertyCategory(listing.propertyCategory)}
                    </span>
                  )}
                </div>
                <h2 className="text-3xl font-bold text-black">
                  ${priceFormat(listing.price)}
                </h2>
                <p className="text-gray-600">
                  {listing.streetAddr}, {listing.city}, {listing.state}{" "}
                  {listing.zip}
                </p>
              </div>
              <div className="text-center">
                {isLive() ? (
                  <>
                    <div className="text-primary text-4xl mb-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-10 w-10 mx-auto"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                      </svg>
                    </div>
                    <p className="text-primary font-medium">LIVE</p>
                    <p className="text-gray-500 text-sm">
                      {getDaysOnMarket() === 0
                        ? "TODAY"
                        : `${getDaysOnMarket()} DOM`}
                    </p>
                  </>
                ) : (
                  <Tag color="red">Expired</Tag>
                )}
              </div>
            </div>

            {/* Property Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-4 border-t border-b border-gray-200 mb-6">
              {listing.beds !== null && listing.beds !== undefined && (
                <div>
                  <span className="text-2xl font-bold">{listing.beds}</span>
                  <span className="text-gray-500 ml-1">Beds</span>
                </div>
              )}
              {listing.baths !== null && listing.baths !== undefined && (
                <div>
                  <span className="text-2xl font-bold">{listing.baths}</span>
                  <span className="text-gray-500 ml-1">Baths</span>
                </div>
              )}
              {listing.sqFt && (
                <div>
                  <span className="text-2xl font-bold">
                    {listing.sqFt.toLocaleString()}
                  </span>
                  <span className="text-gray-500 ml-1">Sq. Ft.</span>
                </div>
              )}
              {listing.lotSize && (
                <div>
                  <span className="text-2xl font-bold">{listing.lotSize}</span>
                  <span className="text-gray-500 ml-1">Lot Size</span>
                </div>
              )}
              {listing.yearBuilt && (
                <div>
                  <span className="text-2xl font-bold">{listing.yearBuilt}</span>
                  <span className="text-gray-500 ml-1">Year Built</span>
                </div>
              )}
              {listing.acreage && (
                <div>
                  <span className="text-2xl font-bold">{listing.acreage}</span>
                  <span className="text-gray-500 ml-1">Acres</span>
                </div>
              )}
            </div>

            {/* Amenities */}
            {listing.amenities && listing.amenities.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Amenities</h3>
                <ul className="list-disc list-inside text-gray-700">
                  {listing.amenities.map((amenity) => (
                    <li key={amenity._id}>{amenity.title}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Agent */}
            {listing.user && (
              <div className="mb-6 border-t pt-4">
                <h3 className="text-lg font-semibold mb-2">Agent</h3>
                <p className="text-primary font-medium">
                  {listing.user.firstName} {listing.user.lastName}
                </p>
                {listing.user.email && (
                  <a
                    href={`mailto:${listing.user.email}`}
                    className="flex items-center gap-2 text-primary hover:underline text-sm mt-1"
                  >
                    <MailOutlined />
                    {listing.user.email}
                  </a>
                )}
                {listing.user.phone && (
                  <a
                    href={`tel:${listing.user.phone}`}
                    className="flex items-center gap-2 text-primary hover:underline text-sm mt-1"
                  >
                    <PhoneOutlined />
                    {listing.user.phone}
                  </a>
                )}
              </div>
            )}

            {/* Notes */}
            {listing.notes && (
              <div className="mb-6 border-t pt-4">
                <h3 className="text-lg font-semibold mb-2">Notes</h3>
                <p className="text-gray-700">{listing.notes}</p>
              </div>
            )}

            {/* Buyer Commission */}
            {listing.buyer_commission && (
              <div className="mb-6 border-t pt-4">
                <h3 className="text-lg font-semibold mb-2">
                  Buyer's Agent Commission
                </h3>
                <p className="text-gray-700">{listing.buyer_commission}</p>
              </div>
            )}

            {/* Links */}
            {listing.links && listing.links.length > 0 && (
              <div className="mb-6 border-t pt-4">
                <h3 className="text-lg font-semibold mb-2">Links</h3>
                <ul className="space-y-1">
                  {listing.links.map((link, index) => (
                    <li key={index}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {link.title || link.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Listing History */}
            <div className="mb-6 border-t pt-4">
              <h3 className="text-lg font-semibold mb-2">Listing History</h3>
              <div className="flex justify-between">
                <span className="text-gray-600">Created</span>
                <span className="text-primary">
                  {listing.createdAt ? fullDate(listing.createdAt) : "-"}
                </span>
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-gray-600">Goes Live on MLS</span>
                <span className="text-primary">
                  {listing.expiration ? fullDate(listing.expiration) : "-"}
                </span>
              </div>
            </div>

            {/* Open Houses */}
            {listing.openHouses && listing.openHouses.length > 0 && (
              <div className="mb-6 border-t pt-4">
                <h3 className="text-lg font-semibold mb-2">Open Houses</h3>
                {listing.openHouses.map((oh, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded mb-2">
                    <p>
                      <span className="font-medium">Start:</span>{" "}
                      {new Date(oh.startTime).toLocaleString()}
                    </p>
                    <p>
                      <span className="font-medium">End:</span>{" "}
                      {new Date(oh.endTime).toLocaleString()}
                    </p>
                    {oh.needHost && (
                      <Tag color="orange" className="mt-1">
                        Needs Host
                      </Tag>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Back Button */}
            <div className="border-t pt-4">
              <button
                onClick={() => navigate(`/company/${companyId}/agent/listings`)}
                className="text-primary hover:underline"
              >
                &larr; Back to Listings
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AgentListingView;
